import { type AxiosInstance } from 'axios';

import axiosServerCreator from '@framework/api/apiCalls.server';
import { DROP_LAST_VISITED } from '@framework/constants/parameters';

import { type AuthenticationStatus } from '@accounts/types/authentication';

import { type Member } from '@type/members';
import { type NextPageContext } from '@type/next';

type State = {
    authenticationStatus?: AuthenticationStatus;
    loggedMember?: {
        id?: string;
        data?: Member;
    };
};

type GetState = () => State;
type SetState = (state: State) => void;

export type Handler<A1 extends any[], R1> = (
    ctx: NextPageContext,
    contextMethods: {
        call: <A2 extends any[], R2>(handler: Handler<A2, R2>, ...args: A2) => Promise<R2>;
        setState: SetState;
        getState: GetState;
        axiosServer: AxiosInstance;
        redirect: (path: string) => void;
        dropLastVisited: () => void;
    },
    ...args: A1
) => Promise<R1> | R1;

class ServerContext {
    ctx: NextPageContext;

    state: State;

    axiosServer: AxiosInstance;

    constructor(ctx: NextPageContext) {
        this.ctx = ctx;
        this.state = {};
        this.axiosServer = axiosServerCreator(ctx);
    }

    getState: GetState = (): State => this.state;

    setState: SetState = (state) => {
        this.state = {
            ...this.state,
            ...state
        };
    };

    redirect = (path: string) => {
        this.ctx.res?.redirect(path);
    };

    dropLastVisited = () => {
        this.redirect(`/?${DROP_LAST_VISITED}=true`);
    };

    call = async <A extends any[], R>(handler: Handler<A, R>, ...args: A): Promise<R> => {
        const result = await handler(
            this.ctx,
            {
                call: this.call,
                getState: this.getState,
                setState: this.setState,
                axiosServer: this.axiosServer,
                redirect: this.redirect,
                dropLastVisited: this.dropLastVisited
            },
            ...args
        );
        return result;
    };
}

export default ServerContext;
